import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ElementRef, input, viewChild, output } from '@angular/core';
import { ICalendarEvent, ICalendarItem } from '@content/calendar/calendar.component';
import { cloneDeep } from 'lodash';

interface AllDayBlock {
  title: string;
  start: Date;
  end: Date;
  event: ICalendarEvent;
}

interface LayoutOverEvent {
  event: ICalendarEvent; 
  col: number; 
  totalCols: number
  textColor?: string;
}

interface CalendarData {
  title: string;
  allDayRows: AllDayBlock[][];
  id: string;
  timedEventsByDay: LayoutOverEvent[][];
}

@Component({
  selector: 'app-calendar-weekly-view',
  templateUrl: './calendar-weekly-view.component.html',
  styleUrls: ['./calendar-weekly-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CalendarWeeklyViewComponent implements OnInit {
  // keep ref so we can clear the timer on destroy
  private timeMarkerIntervalId: any;
  readonly calendars = input<ICalendarItem[]>([]);
  readonly startDate = input<Date>(new Date());
  readonly eventClick = output<ICalendarEvent>();
  readonly eventMouseOver = output<{
    event: ICalendarEvent;
    mouse: MouseEvent;
}>();
  readonly eventMouseLeave = output<void>();
  readonly scrollContainer = viewChild.required<ElementRef>('scrollContainer');

  math = Math;
  days: Date[] = [];
  weekStart!: Date;
  weekEnd!: Date;
  hours: number[] = Array.from({ length: 24 }, (_, i) => i);
  halfHours: number[] = Array.from({ length: 48 }, (_, i) => i);

  data: CalendarData[] = [];

  currentTimeMarkerPosition: number = this.getCurrentTimeMarkerPosition();
  dayHeaderTitleHeight = 40;
  dayHeaderTimeHeight = 24;
  dayHeaderHeight = this.dayHeaderTitleHeight + this.dayHeaderTimeHeight;
  // Increased slightly to improve readability of all-day rows
  eventHeight = 20;
  timeWidth = 60;
  // Halved from 2400 to 1200 so the time-grid scales down with eventHeight
  timeColHeight = 1200;
  timeSlotHeight = `calc(${this.timeColHeight}px / 24)`;
  timeGridColumns = `${this.timeWidth}px 1fr`;
  calendarGridColumns = `repeat(1, 1fr)`;
  totalAllDayRows: number = 0;
  totalAllDayRowsArray: number[] = [];

  ngOnInit(): void {
    this.setupWeek();
    this.processEvents();
    
    this.timeMarkerIntervalId = setInterval(() => {
      this.currentTimeMarkerPosition = this.getCurrentTimeMarkerPosition();
    }, 60000); // update every minute
  }

  /**
   * Format an hour (0-23) into '8AM' style (no minutes, uppercase AM/PM).
   */
  formatHour(hour: number): string {
    const h = hour % 12 === 0 ? 12 : hour % 12;
    const suffix = hour < 12 ? 'AM' : 'PM';
    return `${h}${suffix}`;
  }

  ngOnDestroy(): void {
    if (this.timeMarkerIntervalId) {
      clearInterval(this.timeMarkerIntervalId);
    }
  }

  ngAfterViewInit(): void {
    const scrollContainer = this.scrollContainer();
    if(scrollContainer)
    {
      const element = scrollContainer.nativeElement;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      const targetScrollTop = maxScrollTop * (1.23 / 3);
      scrollContainer.nativeElement.scrollTop = targetScrollTop;
    }
  }

  ngOnChanges(): void {
    this.setupWeek();
    this.processEvents();
  }

  setupWeek() {
    this.weekStart = this.getWeekStart(this.startDate());
    this.weekEnd = new Date(this.weekStart);
    this.weekEnd.setDate(this.weekEnd.getDate() + 4);
    this.days = Array.from({ length: 5 }, (_, i) => new Date(this.weekStart.getTime() + i * 86400000));
  }

  processEvents() {
    this.calendarGridColumns = `repeat(${this.calendars().length}, 1fr)`;
    this.data = this.calendars().map(calendar => {
      const weekEvents = this.getFilteredEvents(calendar);
      const allDayEvents = weekEvents.filter(e => e.isAllDay || this.isMultipleDayEvent(e));
      const timedEvents = weekEvents.filter(e => !e.isAllDay && !this.isMultipleDayEvent(e));

      // Prepare all-day event blocks (spanning multiple days)
      const allDayBlocks = this.mergeAllDayEvents(allDayEvents, this.weekStart, this.weekEnd);

      // Stack all-day events in rows to avoid overlap
      const allDayRows = this.stackAllDayBlocks(allDayBlocks, this.weekStart);

      // Timed events by day
      const timedEventsByDay = this.days.map(day =>
        this.layoutOverlappingEvents(timedEvents.filter(e => this.isEventOnDay(e, day)).sort((a, b) => a.localStart.getTime() - b.localStart.getTime()))
      );
      return { allDayRows, timedEventsByDay, title: calendar.title, id: calendar.id };
    });
    const allDayRows = this.data.map((d)=>d.allDayRows.length);
    this.totalAllDayRows = Math.max(...allDayRows);
    this.totalAllDayRowsArray = Array.from({ length: this.totalAllDayRows }, (_, i) => i);
  }

  getFilteredEvents(calendar: ICalendarItem): ICalendarEvent[] {
    if (calendar.colors && calendar.colors.length > 0) {
      // Always include holiday events regardless of borough color filters
      return calendar.events.filter((event) =>
        (event.subject && event.subject.toLowerCase().includes('holiday')) || calendar.colors?.includes(event.color ?? '')
      );
    } else {
      // avoid expensive deep clone; return a shallow copy to prevent accidental mutation of original array
      return calendar.events ? calendar.events.slice() : [];
    }
  }

  // trackBy helpers to reduce DOM churn
  trackByCalendar(index: number, calendar: any) {
    return calendar?.id || index;
  }

  trackByDay(index: number, day: Date) {
    return day ? day.toDateString() : index;
  }

  trackByRow(index: number) {
    return index;
  }

  trackByBlock(index: number, block: any) {
    return block?.event?.id || block?.title || index;
  }

  trackByTimedEvent(index: number, layout: any) {
    return layout?.event?.id || index;
  }

  trackByHour(index: number, hour: number) {
    return hour;
  }

  trackByHalfHour(index: number, half: number) {
    return half;
  }

  isMultipleDayEvent(event: ICalendarEvent): boolean {
    // consider strictly greater than 24h as multiple-day; events that are exactly 24h
    // (common for all-day single-day events where end is midnight next day) should
    // not be treated as multi-day here
    return event.localEnd.getTime() - event.localStart.getTime() > 86400000;
  }

  getWeekStart(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay() + 1);
    return d;
  }

  isEventOnDay(event: ICalendarEvent, day: Date): boolean {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);
    return event.localStart <= dayEnd && event.localEnd >= dayStart;
  }

  isToday(day: Date): boolean {
    const now = new Date();
    return (
      day.getFullYear() === now.getFullYear() &&
      day.getMonth() === now.getMonth() &&
      day.getDate() === now.getDate()
    );
  }

  isThisWeek(date: Date): boolean {
    const now = new Date();
    const startOfWeek = new Date(now);
    const endOfWeek = new Date(now);

    // Adjust to start of the week (Sunday)
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    // Adjust to end of the week (Saturday)
    endOfWeek.setDate(now.getDate() + (6 - now.getDay()));
    endOfWeek.setHours(23, 59, 59, 999);

    return date >= startOfWeek && date <= endOfWeek;
  }


  layoutOverlappingEvents(events: ICalendarEvent[]):LayoutOverEvent[] {
    let layouts: { event: ICalendarEvent; col: number; totalCols: number }[] = [];
    let columns: ICalendarEvent[] = [];
    events.forEach(event => {
      let col = 0;
      while (
        columns[col] &&
        columns[col].localEnd > event.localStart
      ) {
        col++;
      }
      columns[col] = event;
      layouts.push({ event, col, totalCols: 0 });
    });
    // For each layout, compute the maximum column index among ALL events that overlap it.
    // This ensures chained overlaps (A overlaps B, B overlaps C) correctly produce the
    // required number of simultaneous columns for the whole overlapping group.
    layouts.forEach((layout, i) => {
      let maxCol = layout.col;
      for (let j = 0; j < layouts.length; j++) {
        if (j === i) continue;
        const other = layouts[j];
        if (
          other.event.localStart < layout.event.localEnd &&
          other.event.localEnd > layout.event.localStart
        ) {
          maxCol = Math.max(maxCol, other.col);
        }
      }
      layout.totalCols = maxCol + 1;
    });
    return layouts;
  }

  mergeAllDayEvents(events: ICalendarEvent[], weekStart: Date, weekEnd: Date): AllDayBlock[] {
    return events.map(e => {
      // normalize start to local date (midnight)
      const start = e.localStart < weekStart ? weekStart : new Date(e.localStart.getFullYear(), e.localStart.getMonth(), e.localStart.getDate());

      // normalize end: treat midnight end (00:00) as exclusive and roll back one day
      const rawEnd = new Date(e.localEnd.getTime());
      if (rawEnd.getHours() === 0 && rawEnd.getMinutes() === 0 && rawEnd.getSeconds() === 0 && rawEnd.getMilliseconds() === 0) {
        rawEnd.setDate(rawEnd.getDate() - 1);
      }
      const endDate = rawEnd > weekEnd ? weekEnd : new Date(rawEnd.getFullYear(), rawEnd.getMonth(), rawEnd.getDate());

      return {
        title: e.subject,
        start,
        end: endDate,
        event: e
      };
    });
  }

  stackAllDayBlocks(blocks: AllDayBlock[], weekStart: Date): AllDayBlock[][] {
    const rows: AllDayBlock[][] = [];
    blocks.forEach(block => {
      let placed = false;
      for (let row of rows) {
        if (!row.some(b => this.blocksOverlap(b, block))) {
          row.push(block);
          placed = true;
          break;
        }
      }
      if (!placed) {
        rows.push([block]);
      }
    });
    return rows;
  }

  blocksOverlap(a: AllDayBlock, b: AllDayBlock): boolean {
    return !(a.end < b.start || b.end < a.start);
  }

  dayDiff(start: Date, end: Date): number {
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.round((e.getTime() - s.getTime()) / 86400000);
  }

  getDayLabel(day: Date): string {
    const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `${DAYS[day.getDay()]} ${day.getDate()}`;
  }

  getCurrentTimeMarkerPosition(): number {
    const now = new Date();
    return ((now.getHours() + now.getMinutes() / 60) / 24) * 100;
  }

  onEventClick(event: ICalendarEvent): void {
    this.eventClick.emit(event);
  }

  onEventMouseOver(event: ICalendarEvent, mouse: MouseEvent): void {
    this.eventMouseOver.emit({event,mouse});
  }

  onEventMouseLeave() {
    this.eventMouseLeave.emit();
  }

  isPlanningEvent(calendarId: string): boolean {
    // return calendarId === 'plan';
    return false;
  }

  isCityHoliday(day: Date, calendar: CalendarData): boolean {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);

    return calendar.allDayRows.flat().some(block =>
      block.title?.toLowerCase()?.includes('holiday') && block.start <= dayEnd && block.end >= dayStart
    );
  }
}